import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyingEmail, setVerifyingEmail] = useState(false);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setEmailVerified(session?.user?.email_confirmed_at != null);
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setEmailVerified(session?.user?.email_confirmed_at != null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (
  email: string,
  password: string,
  referralCode?: string,
  firstName?: string,
  lastName?: string,
  country?: string
) => {
  try {
    // Step 1: Create the auth user
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: undefined // no verification
      }
    });

if (data.user && !error) {
  try {
    // Update the user to be a demo user
    const { error: updateError } = await supabase
      .from('users')
      .update({
        is_demo: true,
        first_name: firstName || null,
        last_name: lastName || null,
        country: country || null,
        referred_by: referralCode ? null : null // TODO: Implement referral code lookup
      })
      .eq('id', data.user.id);

    if (updateError) {
      console.error('Error setting user as demo:', updateError);
    }

    // Create initial demo balance for the user
    const { error: balanceError } = await supabase
      .from('balances')
      .insert([{
        user_id: data.user.id,
        usdt_balance: 100000.00, // Demo balance: $100,000 USDT
        btc_balance: 0.00000000  // Demo balance: 0 BTC
      }]);

    if (balanceError) {
      console.error('Error creating demo balance:', balanceError);
    }

    console.log('Successfully set up new user as demo user with demo balance');
  } catch (setupError) {
    console.error('Error during demo user setup:', setupError);
    // Don't throw here as the user was created successfully
  }
}

if (error || !data?.user) {
  return { data, error };
}

      return { data, error };
    return { data, error };
  } catch (error) {
    console.error('Error during sign up:', error);
    return {
      data: { user: null, session: null },
      error: new Error('An error occurred during sign up')
    };
  }
};


  const verifyEmail = async (email: string, otp: string) => {
    // Email verification is disabled
    return { success: true, message: "Email verification is disabled" };
  };

  const resendVerificationOtp = async (email: string) => {
    // Email verification is disabled
    return { success: true, message: 'Email verification is disabled' };
  };

  const signIn = async (email: string, password: string) => {
    return await supabase.auth.signInWithPassword({ email, password });
  };

  // New function to complete sign-in with 2FA
  const completeSignInWith2FA = async (email: string, password: string, twoFactorCode: string) => {
    // Bypass 2FA verification and directly sign in
    return await supabase.auth.signInWithPassword({
      email,
      password,
    });
  };

  const signOut = async () => {
    return await supabase.auth.signOut();
  };

  const updatePassword = async (newPassword: string) => {
    return await supabase.auth.updateUser({
      password: newPassword,
    });
  };

  const resetPasswordForEmail = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        throw error;
      }
      
      return { error: null };
    } catch (error: any) {
      console.error('Error sending password reset email:', error);
      return { error };
    }
  };

  return {
    user,
    session,
    loading,
    emailVerified,
    verifyingEmail,
    signUp,
    signIn,
    completeSignInWith2FA,
    signOut,
    updatePassword,
    resetPasswordForEmail,
    verifyEmail,
    resendVerificationOtp
  };
};

